'use client';

import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from './ui/button';
import { FormLabel, FormField, FormItem, FormControl, FormMessage, Form } from './ui/form';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useForm } from 'react-hook-form';
import { signIn } from 'next-auth/react';
import { useToast } from './ui/use-toast';
import { useRouter } from 'next/navigation';

const formSchema = z.object({
  email: z.string().min(2, {
    message: 'Email must be at least 2 characters.',
  }),
  password: z.string().min(2, {
    message: 'Password must be at least 2 characters.',
  }),
  csrfToken: z.string().optional(),
});

export default function AuthForm({ csrfToken }: { csrfToken?: string }) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { toast } = useToast();
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      csrfToken,
    },
  });

  // 2. Define a submit handler.
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setLoading(true);
    void signIn('credentials', {
      email: values.email,
      password: values.password,
      redirect: false,
    })
      .then(resp => {
        setLoading(false);
        if (!resp?.ok) {
          toast({
            title: 'Gagal Login',
            description: 'Username atau Password salah!',
          });
          return;
        }
        toast({
          title: `Selamat Datang!`,
        });
        void router.push('/');
      })
      .catch(e => {
        console.log(e);
        setLoading(false);
        toast({
          title: 'Maaf masalah terjadi',
          description: 'Segera hubungi developer 😭',
        });
      });
  };

  return (
    <Form {...form}>
      <div className="my-4 flex flex-col gap-1">
        <FormLabel className="text-2xl">Selamat datang</FormLabel>
        <Label className="font-light text-secondary-foreground">
          Masuk menggunakan akun{' '}
          <div className="inline whitespace-nowrap font-semibold text-amber-500">Jubelio</div> kamu!
        </Label>
      </div>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input placeholder="username atau email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="relative">
              <Label
                className="absolute right-4 translate-y-[50%] text-gray-400"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeIcon size={20} /> : <EyeOffIcon size={20} />}
              </Label>
              <FormControl>
                <Input placeholder="password" type={showPassword ? 'text' : 'password'} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button isLoading={loading} type="submit" className="w-full">
          Masuk
        </Button>
      </form>
    </Form>
  );
}
